/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import local_storageImg from 'assets/images/local-storage.png'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - Хранение информации на клиенте</h2>
    <p className={styles.description}>
      <b>Основная проблема веб-приложений</b> — при использовании приложения и закрытии, его состояние будет сброшено
      при следующем посещении. Если вы закрываете приложение на рабочем столе и снова открываете его, последнее
      состояние восстанавливается.{' '}
    </p>
    <p className={styles.description}>
      Вот почему, как разработчику, вам нужно где-то сохранить состояние своего интерфейса или данные которые необходимо
      запомнить между посещениями страницы. На помощь приходит хранение информации о состоянии приложения на компьютере
      пользователя, в браузере.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Cookies</h3>
      <p className={styles.description}>
        Классический способ хранения информации на клиенте это использовать <b>куки-файл</b> размещенный на компьютере
        пользователя. Куки это текстовые строки списка пар <code className={styles.code}>ключ=значение</code>{' '}
        разделенные точкой с запятой. Обычно куки приходят от сервера и позволяют хранить простые строчные данные.{' '}
      </p>
      <p className={styles.description}>Но у куки есть несколько ограничений. </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>Они добавляются к каждому запросу на сервер, что может бы излишне </li>
        <li className={styles.list__item}>Куки позволяют хранить всего до 4кб данных, что довольно мало </li>
        <li className={styles.list__item}>
          Поскольку куки используются для отслеживания поведения людей в серфинге, пользователи часто отключают их, а
          сайты просят каждый раз нужно ли их устанавливать{' '}
        </li>
        <li className={styles.list__item}>
          Используя куки, довольно проблематично отслеживать две или более транзакций на одном и том же сайте, что может
          происходить одновременно в разных вкладках{' '}
        </li>
      </ul>
      <section className={styles.subsection}>
        <h3 className={styles.h3}>4.1.1 - Дополнительные материалы</h3>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <a
              className={styles.link}
              href="https://www.youtube.com/watch?v=I01XMRo2ESg&feature=youtu.be"
              target="_blank"
              rel="noopener noreferrer"
            >
              What is a cookie?{' '}
            </a>
          </li>
          <li className={styles.list__item}>
            <a
              className={styles.link}
              href="https://www.youtube.com/watch?v=LHSSY8QNvew&feature=youtu.be"
              target="_blank"
              rel="noopener noreferrer"
            >
              Follow the Cookie Trail - Computerphile
            </a>
          </li>
        </ul>
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Web Storage API</h3>
      <p className={styles.description}>
        <a
          className={styles.link}
          href="https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API"
          target="_blank"
          rel="noopener noreferrer"
        >
          Web Storage API{' '}
        </a>
        — включает в себя локальное хранилище (localStorage) и хранилище сеансов (sessionStorage), и предоставляет
        способ хранения пар <code className={styles.code}>ключ:значение</code> более интуитивно понятным способом, чем
        использование куки. Данные в веб-хранилище, в отличие от куки, сохраняются даже при закрытии браузера или
        выключении компьютера.
      </p>
      <p className={styles.description}>
        Веб-хранилище это часть HTML5-спецификации. Существует два варианта хранения данных:{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <b>sessionStorage</b> — используется для добавления данных в хранилище сеансов, и данные этого хранилища будут
          доступны для любой страницы с того же сайта, открытого в этом окне, т.е. сессии, и как только окно будет
          закрыто, сеанс будет завершен, а хранилище сеансов очищено.{' '}
        </li>
        <li className={styles.list__item}>
          <b>localStorage</b> — хранилище, которое охватывает несколько окон и сохранятеся после текущего сеанса. В
          частности, веб-приложения могут сохранить пользовательские данные, такие как настройки профиля или содержимое
          корзины товаров на стороне клиента.
        </li>
      </ul>
      <section className={styles.subsection}>
        <h3 className={styles.h3}>4.2.1 - localStorage</h3>
        <p className={styles.description}>
          <b>localStorage (локальное хранилище)</b> — позволяет хранить данные без истечения срока действия в формате
          пар <code className={styles.code}>ключ:значение</code> на компьютере пользователя и читать их, когда
          пользователь снова вернется на страницу.{' '}
        </p>
        <p className={styles.notificationWarn}>
          В <b>localStorage</b> не хранят методы объектов или функции, только данные.{' '}
        </p>
        <p className={styles.description}>
          Использование локального хранилища в современных браузерах очень просто и доступно через интерфейс{' '}
          <code className={styles.code}>window.localStorage</code>.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h3 className={styles.h3}>4.2.2 - Сохранение</h3>
        <CopyBlock text={`localStorage.setItem(key, value)`} language="js" showLineNumbers={false} theme={ocean} />
        <p className={styles.description}>
          Используя метод <code className={styles.code}>setItem</code> можно записать пару{' '}
          <code className={styles.code}>ключ:значение</code>.{' '}
        </p>
        <CopyBlock
          text={`localStorage.setItem('theme', 'light');`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Одним из неприятных недостатков локального хранилища является то, что вы можете хранить только строки. Это
          означает, что когда есть объект, он не будет сохранен правильно. Это можно обойти, используя метод{' '}
          <code className={styles.code}>JSON.stringify</code>, преобразовав объект в строку.{' '}
        </p>
        <CopyBlock
          text={`const settings = {
  theme: 'dark',
  isAuthenticated: true,
  options: [1, 2, 3],
};

localStorage.setItem('settings', JSON.stringify(settings));`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h3 className={styles.h3}>4.2.3 - Чтение</h3>
        <p className={styles.description}>
          Чтение данных из локального хранилища — это тот же процесс, что и сохранение данных, но в обратном порядке.{' '}
        </p>
        <CopyBlock text={`localStorage.getItem(key)`} language="js" showLineNumbers={false} theme={ocean} />
        <p className={styles.description}>
          Если вы знаете, что значение это обычная строка, вы можете просто прочитать ее без необходимости парсить
          что-либо.{' '}
        </p>
        <CopyBlock
          text={`const theme = localStorage.getItem('theme');`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          В противном случае, необходимо распарсить значение, чтобы вернуть исходный JavaScript-объект.{' '}
        </p>
        <CopyBlock
          text={`const settings = {
  theme: 'dark',
  isAuthenticated: true,
  options: [1, 2, 3],
};

localStorage.setItem('settings', JSON.stringify(settings));

const savedSettings = localStorage.getItem('settings');
const parsedSettings = JSON.parse(settingsFromLS);

console.log(parsedSettings);`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h3 className={styles.h3}>4.2.4 - Удаление</h3>
        <CopyBlock text={`localStorage.removeItem(key)`} language="js" showLineNumbers={false} theme={ocean} />
        <p className={styles.description}>
          Для того чтобы удалить пару, необходимо вызвать метод и передать ему ключ.{' '}
        </p>
        <CopyBlock text={`localStorage.removeItem('theme');`} language="js" showLineNumbers={false} theme={ocean} />
      </section>
      <section className={styles.subsection}>
        <h3 className={styles.h3}>4.2.5 - Очистка хранилища</h3>
        <p className={styles.description}>
          Если вы хотите полностью очистить хранилище, вызовите метод <code className={styles.code}>clear</code>.{' '}
        </p>
        <CopyBlock text={`localStorage.clear();`} language="js" showLineNumbers={false} theme={ocean} />
      </section>
      <section className={styles.subsection}>
        <h3 className={styles.h3}>4.2.6 - Пример</h3>
        <p className={styles.description}>
          Создадим поле для вывода и кнопку для сохранения введенного в{' '}
          <code className={styles.code}>localStorage</code>. Изначально такой пары в хранилище нету поэтому будет
          выведена пустая строка.{' '}
        </p>
        <p className={styles.description}>
          Попробуйте позаполнять <code className={styles.code}>input</code> и нажмите{' '}
          <code className={styles.code}>Save</code>. Текст в поле вывода изменится на тот что ввели. Перезагрузите
          страницу, все тот же текст, хотя вы ничего еще не вводили. Мы берем его из{' '}
          <code className={styles.code}>localStorage</code>, последнее веденное значение.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="module-12-localstorage"
          src="https://codepen.io/goit-fe-adv/embed/veORaG?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
        <p className={styles.description}>
          Посмотреть содержимое <code className={styles.code}>localStorage</code> можно в инструментах разработчика на
          вкладке <code className={styles.code}>Application</code>.{' '}
        </p>
        <img className={styles.img} src={local_storageImg} alt="local-storage" />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.3 - Модуль для localStorage</h3>
      <p className={styles.description}>
        Мы уже умеем работать с локальным хранилищем, можем записать, прочитать или удалить. Следующим шагом будет его
        правильное, осмысленное использование.{' '}
      </p>
      <p className={styles.description}>
        Напишем модуль для безопасной работы с <code className={styles.code}>localStorage</code>, ведь при чтении или
        парсе данных для записи может произойти ошибка. Пишем две функции для записи и чтения, назовем их{' '}
        <code className={styles.code}>load</code> и <code className={styles.code}>save</code>.{' '}
      </p>
      <p className={styles.description}>
        В случае, если данные некорректны, <code className={styles.code}>JSON.parse()</code> и{' '}
        <code className={styles.code}>JSON.stringify()</code> генерируют ошибку, то есть скрипт «упадёт», поэтому при
        работе с JSON используем <code className={styles.code}>try..catch</code>, чтобы обработать возможную ошибку и
        выводим <code className={styles.code}>error</code> в консоль.{' '}
      </p>
      <CopyBlock
        text={`// storage.js

// Принимает ключ \`key\` по которому будет произведена выборка.
const load = key => {
  try {
    const serializedState = localStorage.getItem(key);

    return serializedState === null ? undefined : JSON.parse(serializedState);
  } catch (err) {
    console.error('Get state error: ', err);
  }
};

// Принимает ключ \`key\` и значение \`value\`.
const save = (key, value) => {
  try {
    const serializedState = JSON.stringify(value);
    localStorage.setItem(key, serializedState);
  } catch (err) {
    console.error('Set state error: ', err);
  }
};

export default { load, save };`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Теперь мы можем безопасно записывать и читать <code className={styles.code}>localStorage</code>. Попробуйте сами
        дописать метод remove, аналогично <code className={styles.code}>load</code> и{' '}
        <code className={styles.code}>save</code>.
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.4 - IndexedDB</h3>
      <p className={styles.description}>
        <b>IndexedDB</b> — это хранилище больше чем локальное, и полезно для приложений, требующих хранения большого
        количества данных, например кеширование списка статей. Благодаря этому, приложения могут работать и загружаться
        быстрее.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Как и <code className={styles.code}>localStorage</code> сохраняет пары{' '}
          <code className={styles.code}>ключ:значение</code>{' '}
        </li>
        <li className={styles.list__item}>
          В отличии от <code className={styles.code}>localStorage</code> позволяет хранить сложные типы данных{' '}
        </li>
        <li className={styles.list__item}>
          Это{' '}
          <a
            className={styles.link}
            href="http://sernam.ru/book_cbd.php?id=2"
            target="_blank"
            rel="noopener noreferrer"
          >
            не реляционная{' '}
          </a>
          база данных{' '}
        </li>
        <li className={styles.list__item}>Имеет, в основном, асинхронный API на промисах </li>
      </ul>
      <p className={styles.description}>
        Ниже приведен набор полезных статей. Не спешите углубляться в <code className={styles.code}>IndexedDB</code>,
        данная технология упоминается для общего ознакомления, нет смысла использовать это хранилище для тривиальных
        задач, для этого хватит <code className={styles.code}>localStorage</code>.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a className={styles.link} href="https://habr.com/ru/post/213515/" target="_blank" rel="noopener noreferrer">
            Готовим IndexedDB
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://developers.google.com/web/ilt/pwa/working-with-indexeddb"
            target="_blank"
            rel="noopener noreferrer"
          >
            Working with IndexedDB
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API"
            target="_blank"
            rel="noopener noreferrer"
          >
            IndexedDB API на MDN
          </a>
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.5 - Дополнительные материалы</h3>
      <p className={styles.description}>
        <a
          className={styles.link}
          href="http://prgssr.ru/development/obzor-sredstv-hraneniya-dannyh-na-klientskoj-storone.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          Обзор средств хранения данных на стороне клиента
        </a>
      </p>
    </section>
  </section>
))
